import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import { apiRequest } from '../api';
import { ABG } from './abg';
import ModalPortal from './ModalPortal';
import API_BASE_URL from '../apiConfig';
import audioManager from './AudioManager';
import './GameComponent.css'; 
import {
  commonIcons,
  gameLogos,
  getFlag,
  preloadAssets,
  getAssetPaths
} from './assetImports';

const GameComponent = ({ wallets, setWallets, isTelegramMiniApp, referrerId}) => {
    const { t } = useTranslation('game'); 
    const [message, setMessage] = useState('');
    const [showBetOptions, setShowBetOptions] = useState(false);
    const [showSearchingPopup, setShowSearchingPopup] = useState(false);
    const [gameMode, setGameMode] = useState('');
    const navigate = useNavigate();
    const { user, socket, updateUserDetails, fetchUserDetails, setIsGameLoading } = useContext(AuthContext);
    const [score, setScore] = useState(user ? user.score : 900); // Initialize score state from user context
    const [elapsedTime, setElapsedTime] = useState('00:00');
    const [isClosing, setIsClosing] = useState(false);
    const [gameStatus, setGameStatus] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState(isTelegramMiniApp ? 'stars' : 'usdt');
    const [selectedMode, setSelectedMode] = useState(0); 
    const [isDragging, setIsDragging] = useState(false);
    const [tempMessage, setTempMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const soundsPreloadedRef = useRef(false);
    const [pendingInvite, setPendingInvite] = useState(null);
    const [showFriendInvitePopup, setShowFriendInvitePopup] = useState(false);
    const [isPrivateSearch, setIsPrivateSearch] = useState(false);


 const checkInvite = async () => {
  if (!user) return;

  const myId = user._id;
  const refId = referrerId;

  try {

    const resMyInvite = await fetch(
      `${API_BASE_URL}/api/check-friend-invite?inviterId=${myId}`
    );
    const myData = await resMyInvite.json();

    if (myData.waiting) {
      fetchUserDetails();
      setMessage(t('waiting_friend'));
      setIsPrivateSearch(true);
      setShowSearchingPopup(true);
      return; 
    }

    if (refId) {
      const resRefInvite = await fetch(
        `${API_BASE_URL}/api/check-friend-invite?inviterId=${refId}`
      );
      const refData = await resRefInvite.json();

      if (refData.waiting) {
        setPendingInvite(refData);
        setGameMode(refData.gameMode);
        setShowFriendInvitePopup(true);
        return;
      }
    }

    setShowSearchingPopup(false);

  } catch (err) {
    console.error("Friend invite check failed:", err);
  }
};



useEffect(() => {
  if (!user) return;

  checkInvite();

}, []);





    const vibrate = (pattern) => {    
        if ('vibrate' in navigator) {
            navigator.vibrate(pattern);
        }
    };

    const handleClosePopup = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsClosing(true);
        setTimeout(() => {
            setShowBetOptions(false);
            setIsClosing(false);
            setGameMode('');
        }, 0);
    };

    useEffect(() => {
    if (soundsPreloadedRef.current) return;

    preloadSounds();
    
    }, []);

  const preloadSounds = async () => {

      try {
        await audioManager.preloadSounds([
          { name: 'silent', path: '/sounds/silent.mp3' },
          { name: 'playerCardPlay', path: '/sounds/card_play.mp3' },
          { name: 'opponentCardPlay', path: '/sounds/card_play.mp3' },
          { name: 'playerDealCard', path: '/sounds/deal_card.mp3' },
          { name: 'playerDealRem_card', path: '/sounds/dealRem_card.mp3' },
          { name: 'opponentDealCard', path: '/sounds/deal_card.mp3' },
          { name: 'opponentDealRem_card', path: '/sounds/dealRem_card.mp3' },
          { name: 'faceCardSound', path: '/sounds/faceCard.mp3' },
          { name: 'gameStart', path: '/sounds/game_start.mp3' },
          { name: 'buttonClick', path: '/sounds/buttonClick.mp3' },
          { name: 'lose', path: '/sounds/lose.mp3' },
          { name: 'win', path: '/sounds/win.mp3' },
          { name: 'checkerMove', path: '/sounds/checker_move.mp3' },
          { name: 'coinCollision', path: '/sounds/coin_collision.mp3' },
          { name: 'coinPocketed', path: '/sounds/coin_pocketed.mp3' },
          { name: 'diceRolling', path: '/sounds/rolling_dice.mp3' },
          { name: 'placeBet', path: '/sounds/poker_bet.mp3' },
          { name: 'moveToPot', path: '/sounds/poker_moveToPot.mp3' },
          { name: 'moveToWinner', path: '/sounds/poker_moveToWinner.mp3' },
        ]);
        soundsPreloadedRef.current = true;
        audioManager.enableSoundOnUserInteraction();
      } catch (error) {
        console.error('[App] Error during sound preloading:', error);
      }
    };

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        setScore(user.score); 
      
        const handleGameStart = async (data) => {
            if (!data.game || !data.game.gameMode) {
                console.error('Game mode missing from data:', data);
            return;
            }
            if (data.game.status === 'in-progress') {
                setShowSearchingPopup(false);
                setGameStatus('in-progress');
                setIsGameLoading(true);
                try {
                    await preloadAssets(getAssetPaths(data.game.gameMode));
                    setIsGameLoading(false);
                    navigate(`/${data.game.gameMode}`);
                } catch (error) {
                    console.error("Failed to preload images", error);
                    setIsGameLoading(false);
                }
            }
        };

        const handlePlayerReconnected = ({ userId, gameMode }) => {
            if (userId === user._id) {
                navigate(`/${gameMode}`);
            }
        };

        const handleSearchTimeout = (data) => {
            
            setWallets(data.wallets);
            setShowSearchingPopup(false);
            setGameMode('');
            if(user) checkInvite();
        };

        socket.on('gameStarted', handleGameStart);
        socket.on('playerReconnected', handlePlayerReconnected);
        socket.on('search-timeout', handleSearchTimeout);

        return () => {
            socket.off('gameStarted', handleGameStart);
            socket.off('playerReconnected', handlePlayerReconnected);
            socket.off('search-timeout', handleSearchTimeout);
        };
    }, [socket, user, setIsGameLoading, updateUserDetails]);

    useEffect(() => {
        if (!showSearchingPopup && gameStatus === 'in-progress') {
            vibrate(400);
        }
    }, [showSearchingPopup, gameStatus, navigate]);

  const handleInviteFriend = async (amount, currency) => {
  const tg = window.Telegram?.WebApp;

  // ✅ Prepare invite links
  const telegramInviteLink =
    `https://t.me/TG_Game_stars_bot?start=${user._id}`;
  const fallbackInviteLink =
    `https://gamestar24.com/register?invitedBy=${user._id}`;


  const shareText = t('share_text', { game: t(`games.${gameMode}`) })
 || "💰 Join me in GameStar24 and play-to-earn with real rewards!";

  try {
    
    if (tg?.initData) {
      const telegramShareLink = `https://t.me/share/url?url=${encodeURIComponent(telegramInviteLink)}&text=${encodeURIComponent(shareText)}`;
      window.open(telegramShareLink, '_blank');
    } else if (navigator.share) {
      await navigator.share({
        title: 'Join me on GameStar24!',
        text: shareText,
        url: fallbackInviteLink,
      });
    } else {
      await navigator.clipboard.writeText(fallbackInviteLink);
      alert(t('alerts.link_copied') || 'Invite link copied!');
    }

  
    await startGame(amount, currency, true);

  } catch (err) {
    console.error('Invite friend error:', err);
    alert(t('alerts.invite_failed') || 'Failed to send invite.');
  }
};



        
    const startGame = async (amount, currency = selectedCurrency , isPrived = false, referrerId = null , mode = '') => {
        if (amount === null) {
            setTempMessage(t('errors.select_mode'));
            setMessageType('error');
            setTimeout(() => {
                setTempMessage('');
                setMessageType('');
            }, 2500);
            return;
        }
        if ( !gameMode && !mode ) {
            setTempMessage(t('errors.select_game'));
            setMessageType('error');
            setTimeout(() => {
                setTempMessage('');
                setMessageType('');
            }, 2500);
            return;
        }
       

        if (wallets[currency] < amount) {
            setTempMessage(t('errors.insufficient_balance'));
            setMessageType('error');
            setTimeout(() => {
                setTempMessage('');
                setMessageType('');
            }, 2500);
            return;
        }

        const socketId = socket.id;

        if (!socket || !socket.connected){
            fetchUserDetails();
        }

        try {
            const response = await apiRequest(`${API_BASE_URL}/api/start-game`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    betAmount: amount, 
                    gameMode: gameMode || mode, 
                    currency,  
                    socketId,
                    isPrived,
                    referrerId 
                })
            });

            const data = await response.json();

            if (response.ok) {
                setIsPrivateSearch(isPrived);
                !isPrived? setMessage(t('searching_opponent')) : setMessage(t('waiting_friend')) ;
                setWallets(prev => ({
                    ...prev,
                    [currency]: prev[currency] - amount
                }));
                setShowBetOptions(false);
                setShowSearchingPopup(true);
            } else {
                setTempMessage(data.message || t('errors.start_failed'));
                setMessageType('error');
                setTimeout(() => {
                    setTempMessage('');
                    setMessageType('');
                }, 2500);
            }
        } catch (err) {
            setTempMessage(t('errors.generic'));
            setMessageType('error');
            setTimeout(() => {
                setTempMessage('');
                setMessageType('');
            }, 2500);
            console.error('Error starting game:', err);
        }
    };

    const handleStartGameClick = (gameMode) => {
        if (!gameMode) return;
        setGameMode(gameMode);
        audioManager.playSound('silent');
        setMessage('');
        setTempMessage('');
        setMessageType('');
        setShowBetOptions(true);
    };

    const handleDepositClick = () => {
        navigate('/deposit-withdraw-All');
    };

    const handleTokenClick = () => {
        navigate('/Token-To-Usdt');
    };

    const handleProfileClick = () => {
        navigate('/profile');
    };

    const renderHomeAvatar = (name, photoUrl, customAvatar) => {
        if (customAvatar) {
        return <img src={customAvatar} alt={`${name}'s Custom Avatar`} className="avatar-image" />;
        }
        if (photoUrl) {
            return <img src={photoUrl} alt={`${name}'s Avatar`} className="avatar-image" />;
        }
        const initial = name?.charAt(0).toUpperCase() || 'P'; 
        const avatarStyle = ABG(name); 

        return (
            <div className="avatar-placeholder" style={avatarStyle}>
                {initial}
            </div>
        );
    }

    const conversionRate = 100; 

    const betOptions = [
        { amount: 25, win: 50, currency: 'token' },
        { amount: 300, win: 500 },
        { amount: 1200, win: 2000 },
        { amount: 6000, win: 10000 }
    ];
    const getDisplayAmount = (amount) => {
        return isTelegramMiniApp ? amount : (amount / conversionRate);
    };

    useEffect(() => {
        if (!isDragging || !showBetOptions) return;
    
        const handleMove = (e) => {
            e.preventDefault(); 
            e.stopPropagation();
    
            const clientX = e.clientX || e.touches?.[0]?.clientX;
            if (!clientX) return;
    
            const sliderTrack = document.querySelector(".slider-track");
            const newMode = calculateSelectedMode(clientX, sliderTrack);
            setSelectedMode(newMode);
        };
    
        const handleEnd = () => setIsDragging(false);
    
        const blockTouch = (e) => {
            if (e.target.closest(".slider-track") && !e.target.closest(".bet-popup-overlay")) {
                e.preventDefault();
            }
        };
    
        window.addEventListener("touchmove", handleMove, { passive: false });
        window.addEventListener("touchend", handleEnd, { passive: false });
        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleEnd);
        window.addEventListener("touchstart", blockTouch, { passive: false });
    
        return () => {
            window.removeEventListener("touchmove", handleMove);
            window.removeEventListener("touchend", handleEnd);
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleEnd);
            window.removeEventListener("touchstart", blockTouch);
        };
    }, [isDragging, showBetOptions]);

    useEffect(() => {
        const preventScroll = (e) => e.preventDefault();
    
        if (showBetOptions || showSearchingPopup) {
            document.body.style.overflow = 'hidden';
            document.addEventListener('touchmove', preventScroll, { passive: false });
        } else {
            document.body.style.overflow = '';
            document.removeEventListener('touchmove', preventScroll);
        }
    
        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('touchmove', preventScroll);
        };
    }, [showBetOptions, showSearchingPopup]);
    
    const calculateSelectedMode = (clientX, trackElement) => {
        const rect = trackElement.getBoundingClientRect();
        const clickX = clientX - rect.left;
        const trackWidth = rect.width;
        const percent = (clickX / trackWidth) * 100;
        const adjustedPercent = (percent - 5) / 30; // 5% offset, 30% steps
        return Math.round(Math.min(Math.max(adjustedPercent, 0), 3));
    };

    const handleTrackClick = (e) => {
        const clientX = e.clientX || e.touches?.[0]?.clientX;
        if (!clientX) return;
    
        const sliderTrack = document.querySelector(".slider-track");
        const newMode = calculateSelectedMode(clientX, sliderTrack);
        setSelectedMode(newMode);
    };

    const isTestUser = user && (
        user._id === '6739dd7ba8e4c2bb93ea4d79' || 
        user._id === '673b52fbacdc276a5a50bc5a' || 
        user._id === '68b1b4a72024d80d5d710d13' ||
        user._id === '68bea12a1dd1c9850ce4ea93' ||
        user._id === '68bea96b2bc958fa5ffa0788' ||
        user._id === '673b4f70acdc276a5a502766'
    
    );

    const handleCancelPrivate = async () => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/api/cancel-search`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();

    if (response.ok) {
      setWallets(data.wallets);
      setShowSearchingPopup(false);
      setIsPrivateSearch(false);
      setGameMode('');
      setMessage('');
    } else {
      console.error('Cancel failed:', data.message);
    }

  } catch (err) {
    console.error('Cancel private search error:', err);
  }
};


    
    return (
        
        <div className="game-component">
            <div className="profile-section">
                <div className="main-profile-background">
                    <button className="add-funds-button" onClick={handleDepositClick}>
                        <img src={commonIcons.plusIcon} alt="Add funds" />
                    </button>

                    <div className="profile-avatar" onClick={handleProfileClick}>
                        <div className="avatar-gradient-border"></div>
                        {renderHomeAvatar(user?.nickname, user?.photoUrl, user?.customAvatar)}
                    </div>

                    <div className="nickname-background">
                        <div className="username-with-flag">
                            <span className="username">{user?.nickname || "Player"}</span>
                            {user?.country && <img src={getFlag(user.country)} alt={`${user.country} flag`} className="flag-icon" />}
                        </div>
                    </div>

                    <div className="profile-icon-container" onClick={handleProfileClick}>
                        <img src={commonIcons.userIcon} alt="Profile" className="profile-icon" />
                    </div>

                    <div className="balance-score-background">
                        <div className="balance-section" onClick={handleDepositClick}>
                            <img 
                                src={isTelegramMiniApp ? commonIcons.starIcon : commonIcons.usdtIcon} 
                                alt="currency-icon" 
                                className="Main-balance-currency-icon" 
                            />
                            <div className="balance-value">
                                {isTelegramMiniApp ? (
                                    <span className="main-balance">
                                        {wallets.stars != null ? wallets.stars.toLocaleString() : '0'}
                                    </span>
                                ) : (
                                    <>
                                        <span className="main-balance">
                                            {wallets.usdt != null && !isNaN(wallets.usdt)
                                                ? Math.floor(wallets.usdt).toLocaleString()
                                                : '0'}
                                        </span>
                                        <span className="decimal-point">.</span>
                                        <span className="decimal-balance">
                                            {wallets.usdt != null && !isNaN(wallets.usdt)
                                                ? (Math.floor(wallets.usdt * 100) % 100).toString().padStart(2, '0')
                                                : '00'}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        <div className="score-section" onClick={handleTokenClick}>
                            <img src={commonIcons.tokenYellow} alt="Token" className="score-icon" />
                            <div className="score-value">
                                {wallets.token != null ? wallets.token.toLocaleString() : '0'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="games-section">
                <div className="game-item" data-game="rps" onClick={() => handleStartGameClick('rps')}>
                    <div className="game-logo-container">
                        <img src={gameLogos.rps} className="game-logo-blur-rps" alt="" />
                        <img src={gameLogos.rps} className="game-logo-sharp" alt="Rock Paper Scissors" />
                    </div>
                    <span className="game-title">{t('games.rps')}</span>
                </div>

                <div className="game-item" data-game="blot" onClick={() => handleStartGameClick('blot')}>
                    <div className="game-logo-container">
                        <img src={gameLogos.blot} className="game-logo-blur-blot" alt="" />
                        <img src={gameLogos.blot} className="game-logo-sharp" alt="Belote" />
                    </div>
                    <span className="game-title">{t('games.blot')}</span>
                </div>

                <div className="game-item" data-game="carrom" onClick={() => handleStartGameClick('carrom')}>
                    <div className="game-logo-container">
                        <img src={gameLogos.carrom} className="game-logo-blur-carrom" alt="" />
                        <img src={gameLogos.carrom} className="game-logo-sharp" alt="Carrom" />
                    </div>
                    <span className="game-title">{t('games.carrom')}</span>
                </div>

                <div className="game-item" data-game="backgammon" onClick={() => handleStartGameClick('backgammon')}>
                    <div className="game-logo-container">
                        <img src={gameLogos.backgammon} className="game-logo-blur-backgammon" alt="" />
                        <img src={gameLogos.backgammon} className="game-logo-sharp" alt="Backgammon" />
                    </div>
                    <span className="game-title">{t('games.backgammon')}</span>
                </div>


                          
               
                    <div className="game-item" data-game="poker" onClick={() => handleStartGameClick('poker')}>
                        <div className="game-logo-container">
                            <img src={gameLogos.poker || commonIcons.defaultGameIcon} className="game-logo-blur-poker" alt="" />
                            <img src={gameLogos.poker || commonIcons.defaultGameIcon} className="game-logo-sharp" alt="Poker" />
                        </div>
                        <span className="game-title">{t('games.poker') || 'Poker'}</span>
                    </div>
              

                {/* Domino Game - Only for test users 
                {isTestUser && (
                    <div className="game-item" data-game="domino" onClick={() => handleStartGameClick('domino')}>
                        <div className="game-logo-container">
                            <img src={gameLogos.domino || commonIcons.defaultGameIcon} className="game-logo-blur-rps" alt="" />
                            <img src={gameLogos.domino || commonIcons.defaultGameIcon} className="game-logo-sharp" alt="Domino" />
                        </div>
                        <span className="game-title">{t('games.domino') || 'Domino'}</span>
                    </div>
                )}   */}
            </div>

            {showBetOptions && (
                <>
                    <ModalPortal>
                        <div 
                            className="bet-popup-overlay" 
                            onPointerUp={handleClosePopup}
                           onTouchEnd={handleClosePopup}
                           // onClick={handleClosePopup}
                        ></div>
                        <div className="bet-popup-container">
                            <div className="bet-popup-background"></div>
                            <div className="bet-popup-content">
                                <div className="popup-header">
                                    <h3 className={messageType === 'error' ? 'error-title' : ''}>
                                        {tempMessage || t('choose_mode')}
                                    </h3>
                                </div>
                                
                                <div className="bet-mode-display">
                                    <div className="mode-icon-container">
                                        <div className={`mode-icon-blur ${selectedMode === 0 ? 'free' : selectedMode === 1 ? 'standard' : selectedMode === 2 ? 'pro' : 'expert'}`}></div>
                                        <div className={`mode-icon ${selectedMode === 0 ? 'free' : selectedMode === 1 ? 'standard' : selectedMode === 2 ? 'pro' : 'expert'}`}></div>
                                    </div>
                                </div>
                                <div className="bet-info-container"> 
                                  
                                    <div className="bet-amount">
                                       {t('bet')} {betOptions[selectedMode].currency === 'token' ? (
                                            <>
                                                <div className="bet-token-icon small"></div>
                                                <span>{betOptions[selectedMode].amount}</span>
                                            </>
                                        ) : isTelegramMiniApp ? (
                                            <>
                                                <div className="bet-star-icon small"></div>
                                                <span>{getDisplayAmount(betOptions[selectedMode].amount)}</span>
                                            </>
                                        ) : (
                                            <>
                                                <div className="bet-usdt-icon small"></div>
                                                <span>{getDisplayAmount(betOptions[selectedMode].amount)}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="mode-slider">
                                    <div 
                                        className="slider-track"
                                        onMouseDown={() => {
                                            setIsDragging(true);
                                        }}
                                        onTouchStart={() => {
                                            setIsDragging(true);
                                        }}
                                        onPointerUp={(e) => {
                                            handleTrackClick(e);
                                        }}
                                    >
                                        <div className="slider-clickable-overlay" />
                                        <div className="slider-dots">
                                            <div className="dot"></div>
                                            <div className="dot"></div>
                                            <div className="dot"></div>
                                            <div className="dot"></div>
                                        </div>
                                        <div 
                                            className="slider-handle" 
                                            style={{ 
                                                left: `calc(5% + ${selectedMode * 30}%)`
                                            }}
                                        ></div>
                                    </div>
                                </div>

                                <div className="win-amount">
                                    {t('win')} {betOptions[selectedMode].currency === 'token' ? (
                                        <>
                                            <div className="token-icon small"></div>
                                            <span>{betOptions[selectedMode].win}</span>
                                        </>
                                    ) : isTelegramMiniApp ? (
                                        <>
                                            <div className="star-icon small"></div>
                                            <span>{getDisplayAmount(betOptions[selectedMode].win)}</span>
                                        </>
                                    ) : (
                                        <>
                                            <div className="usdt-icon small"></div>
                                            <span>{getDisplayAmount(betOptions[selectedMode].win)}</span>
                                        </>
                                    )}
                                </div>

                                <button 
                                    className="find-game-button"
                                    onPointerUp={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const option = betOptions[selectedMode];
                                        option.currency === 'token' 
                                            ? startGame(option.amount, 'token')
                                            : startGame(getDisplayAmount(option.amount));
                                    }}
                                >
                                    {t('find_game')}
                                </button>

                                <button 
  className="find-game-button challenge-friend-button"
  onPointerUp={(e) => {
    e.preventDefault();
    e.stopPropagation();
     const option = betOptions[selectedMode];
                                        option.currency === 'token' 
                                            ? handleInviteFriend(option.amount, 'token')
                                            : handleInviteFriend(getDisplayAmount(option.amount), selectedCurrency);
    
  }}
>
  {t('challenge_friend')}
</button>

                            </div>
                        </div>
                    </ModalPortal>
                </>
            )}

            {showSearchingPopup && (
  <ModalPortal>
    <div className="bet-popup-overlay">
      <div className="GameComponent-search-popup-container">
        <div className="GameComponent-search-popup-background"></div>

       <div className="GameComponent-search-popup-content">

  {/* Centered Spinner */}
  <div className="spinner-center-wrapper">
    <div className="GameComponent-loading-spinner"></div>
  </div>

  {/* Text + Cancel button */}
  <div className="bottom-content-wrapper">
    <div className="GameComponent-searching-text">
      {message}
    </div>

    {isPrivateSearch && (
      <button
        className="challenge-friend-button search-cancel-btn"
        onClick={handleCancelPrivate}
      >
        {t('friend_invite_popup.cancel') || 'Cancel'}
      </button>
    )}
  </div>

</div>
      </div>
    </div>
  </ModalPortal>
)}


            {showFriendInvitePopup && pendingInvite && (
                
  <ModalPortal>
    <div className="bet-popup-overlay" >
      <div className="friend-invite-popup">
        <h3>{t('friend_invite_popup.title') || 'Friend waiting for you!'}</h3>
       <p className="friend-invite-details">
  {t('friend_invite_popup.details', {
    game: t(`games.${pendingInvite.gameMode}`),
    bet: ''
  }) || 
  `Game: ${t(`games.${pendingInvite.gameMode}`)} — Bet:`}
  <span className="bet-inline">
    &nbsp;{pendingInvite.betAmount}
    <span className={`bet-${pendingInvite.currency.toLowerCase()}-icon small`} />
  </span>
</p>
        <div className="popup-buttons">
          <button
            className="join-btn"
            onClick={() => {
              
              startGame(pendingInvite.betAmount, pendingInvite.currency, true, pendingInvite.inviterId ,pendingInvite.gameMode);
              setShowFriendInvitePopup(false);
            }}
          >
            {t('friend_invite_popup.join') || 'Join'}
          </button>
          <button className="cancel-btn" onClick={() => setShowFriendInvitePopup(false)}>
            {t('friend_invite_popup.cancel') || 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  </ModalPortal>
)}
        </div>
    );
};

export default GameComponent;